"use client";

import { useState, useEffect, useCallback } from "react";
import {
  Box,
  Card,
  CardContent,
  Typography,
  Grid,
  Container,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  TextField,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  CircularProgress,
  Alert,
  Chip,
  IconButton,
  AppBar,
  Toolbar,
  Fade,
  Zoom,
  useTheme,
  alpha,
  List,
  ListItem,
  ListItemText,
  CardHeader,
  Tooltip,
  Stack,
  LinearProgress,
} from "@mui/material";
import {
  TrendingUp,
  TrendingDown,
  AttachMoney,
  Receipt,
  LocalHospital,
  AccountBalance,
  Assessment,
  DateRange,
  Refresh,
  ArrowBack,
  Visibility,
  CheckCircle,
  Cancel,
  Warning,
  TableChart,
  PictureAsPdf,
} from "@mui/icons-material";

// You'll need to install these libraries:
import jsPDF from "jspdf";
import "jspdf-autotable";
import * as XLSX from "xlsx";

// Import your custom hook and translation
import useFetchObjects from "../../../../api/useFetchObjects.js";
import { useTranslation } from "react-i18next";

// Enhanced Report Card Component
const ReportCard = ({
  title,
  value,
  onClick,
  unit = "",
  icon: Icon,
  color = "primary",
  trend,
  subtitle,
}) => {
  const theme = useTheme();

  const getColorConfig = (color) => {
    const colors = {
      primary: {
        main: theme.palette.primary.main,
        light: theme.palette.primary.light,
        bg: alpha(theme.palette.primary.main, 0.1),
        border: alpha(theme.palette.primary.main, 0.2),
      },
      success: {
        main: theme.palette.success.main,
        light: theme.palette.success.light,
        bg: alpha(theme.palette.success.main, 0.1),
        border: alpha(theme.palette.success.main, 0.2),
      },
      error: {
        main: theme.palette.error.main,
        light: theme.palette.error.light,
        bg: alpha(theme.palette.error.main, 0.1),
        border: alpha(theme.palette.error.main, 0.2),
      },
      info: {
        main: theme.palette.info.main,
        light: theme.palette.info.light,
        bg: alpha(theme.palette.info.main, 0.1),
        border: alpha(theme.palette.info.main, 0.2),
      },
      secondary: {
        main: theme.palette.secondary.main,
        light: theme.palette.secondary.light,
        bg: alpha(theme.palette.secondary.main, 0.1),
        border: alpha(theme.palette.secondary.main, 0.2),
      },
    };
    return colors[color] || colors.primary;
  };

  const colorConfig = getColorConfig(color);

  return (
    <Zoom in timeout={300}>
      <Card
        sx={{
          cursor: "pointer",
          transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
          position: "relative",
          overflow: "hidden",
          "&:hover": {
            transform: "translateY(-8px) scale(1.02)",
            boxShadow: theme.shadows[12],
            "& .card-icon": {
              transform: "rotate(10deg) scale(1.1)",
            },
          },
          background: `linear-gradient(135deg, ${colorConfig.bg} 0%, ${alpha(
            colorConfig.main,
            0.05
          )} 100%)`,
          border: `2px solid ${colorConfig.border}`,
          borderRadius: 3,
          "&::before": {
            content: '""',
            position: "absolute",
            top: 0,
            left: 0,
            right: 0,
            height: "4px",
            background: `linear-gradient(90deg, ${colorConfig.main}, ${colorConfig.light})`,
          },
        }}
        onClick={onClick}
      >
        <CardContent sx={{ p: 3, position: "relative" }}>
          <Box
            display="flex"
            alignItems="center"
            justifyContent="space-between"
            mb={2}
          >
            <Typography
              variant="subtitle1"
              sx={{
                color: "text.secondary",
                fontWeight: 600,
                fontSize: "0.875rem",
                textTransform: "uppercase",
                letterSpacing: "0.5px",
              }}
            >
              {title}
            </Typography>
            {Icon && (
              <Box
                className="card-icon"
                sx={{
                  p: 1.5,
                  borderRadius: 2,
                  backgroundColor: colorConfig.bg,
                  color: colorConfig.main,
                  transition: "all 0.3s ease",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                }}
              >
                <Icon sx={{ fontSize: 28 }} />
              </Box>
            )}
          </Box>

          <Typography
            variant="h3"
            sx={{
              fontWeight: 700,
              color: colorConfig.main,
              mb: 1,
              fontSize: { xs: "1.75rem", sm: "2.125rem" },
              lineHeight: 1.2,
            }}
          >
            {value !== undefined && value !== null
              ? `${unit}${value.toLocaleString()}`
              : "N/A"}
          </Typography>

          {subtitle && (
            <Typography
              variant="body2"
              sx={{
                color: "text.secondary",
                mb: trend ? 1 : 0,
                fontSize: "0.875rem",
              }}
            >
              {subtitle}
            </Typography>
          )}

          {trend && (
            <Box display="flex" alignItems="center">
              {trend > 0 ? (
                <TrendingUp
                  sx={{ color: "success.main", fontSize: 20, mr: 0.5 }}
                />
              ) : (
                <TrendingDown
                  sx={{ color: "error.main", fontSize: 20, mr: 0.5 }}
                />
              )}
              <Typography
                variant="body2"
                sx={{
                  color: trend > 0 ? "success.main" : "error.main",
                  fontWeight: 600,
                }}
              >
                {Math.abs(trend)}%
              </Typography>
            </Box>
          )}
        </CardContent>
      </Card>
    </Zoom>
  );
};

// Enhanced Date Range Selector Component
const DateRangeSelector = ({ onDateRangeChange }) => {
  const { t } = useTranslation();
  const theme = useTheme();
  const [type, setType] = useState("overall");
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");

  const applyDateRange = useCallback(() => {
    const today = new Date();
    const startOfWeek = new Date(today);
    startOfWeek.setDate(today.getDate() - today.getDay());
    const startOfMonth = new Date(today.getFullYear(), today.getMonth(), 1);

    let sDate, eDate;

    if (type === "daily") {
      sDate = today.toISOString().split("T")[0];
      eDate = today.toISOString().split("T")[0];
    } else if (type === "weekly") {
      sDate = startOfWeek.toISOString().split("T")[0];
      eDate = today.toISOString().split("T")[0];
    } else if (type === "monthly") {
      sDate = startOfMonth.toISOString().split("T")[0];
      eDate = today.toISOString().split("T")[0];
    } else if (type === "custom") {
      sDate = startDate;
      eDate = endDate;
    } else {
      sDate = "";
      eDate = "";
    }

    onDateRangeChange({ type, start_date: sDate, end_date: eDate });
  }, [type, startDate, endDate, onDateRangeChange]);

  useEffect(() => {
    applyDateRange();
  }, [type, applyDateRange]);

  return (
    <Card
      sx={{
        mb: 4,
        background: `linear-gradient(135deg, ${alpha(
          theme.palette.primary.main,
          0.05
        )} 0%, ${alpha(theme.palette.primary.main, 0.02)} 100%)`,
        border: `1px solid ${alpha(theme.palette.primary.main, 0.1)}`,
        borderRadius: 3,
      }}
    >
      <CardContent sx={{ p: 3 }}>
        <Box display="flex" alignItems="center" mb={3}>
          <DateRange sx={{ color: "primary.main", mr: 1.5, fontSize: 28 }} />
          <Typography
            variant="h5"
            sx={{ fontWeight: 700, color: "text.primary" }}
          >
            Date Range Filter
          </Typography>
        </Box>

        <Grid container spacing={3} alignItems="end">
          <Grid item xs={12} sm={6} md={3}>
            <FormControl fullWidth variant="outlined">
              <InputLabel>Period</InputLabel>
              <Select
                value={type}
                label="Period"
                onChange={(e) => {
                  setType(e.target.value);
                  if (e.target.value !== "custom") {
                    setStartDate("");
                    setEndDate("");
                  }
                }}
                sx={{
                  "& .MuiOutlinedInput-root": {
                    borderRadius: 2,
                  },
                }}
              >
                <MenuItem value="overall">Overall</MenuItem>
                <MenuItem value="daily">Daily</MenuItem>
                <MenuItem value="weekly">Weekly</MenuItem>
                <MenuItem value="monthly">Monthly</MenuItem>
                <MenuItem value="custom">Custom Range</MenuItem>
              </Select>
            </FormControl>
          </Grid>

          {type === "custom" && (
            <>
              <Grid item xs={12} sm={6} md={3}>
                <TextField
                  type="date"
                  label="Start Date"
                  value={startDate}
                  onChange={(e) => setStartDate(e.target.value)}
                  fullWidth
                  InputLabelProps={{ shrink: true }}
                  sx={{
                    "& .MuiOutlinedInput-root": {
                      borderRadius: 2,
                    },
                  }}
                />
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                <TextField
                  type="date"
                  label="End Date"
                  value={endDate}
                  onChange={(e) => setEndDate(e.target.value)}
                  fullWidth
                  InputLabelProps={{ shrink: true }}
                  sx={{
                    "& .MuiOutlinedInput-root": {
                      borderRadius: 2,
                    },
                  }}
                />
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                <Button
                  variant="contained"
                  onClick={applyDateRange}
                  fullWidth
                  startIcon={<Refresh />}
                  sx={{
                    height: 56,
                    borderRadius: 2,
                    textTransform: "none",
                    fontSize: "1rem",
                    fontWeight: 600,
                  }}
                >
                  Apply Filter
                </Button>
              </Grid>
            </>
          )}
        </Grid>
      </CardContent>
    </Card>
  );
};

// Download Functions
const downloadPDF = (title, data, columns) => {
  const doc = new jsPDF();

  // Add title with better styling
  doc.setFontSize(24);
  doc.setTextColor(33, 150, 243); // Blue color
  doc.text(title, 20, 25);

  // Add subtitle
  doc.setFontSize(12);
  doc.setTextColor(100, 100, 100);
  doc.text(
    `Generated on: ${new Date().toLocaleDateString()} at ${new Date().toLocaleTimeString()}`,
    20,
    35
  );

  // Add a line
  doc.setDrawColor(33, 150, 243);
  doc.setLineWidth(0.5);
  doc.line(20, 40, 190, 40);

  // Prepare table data
  const tableColumns = columns.map((col) => col.header);
  const tableRows = data.map((row) =>
    columns.map((col) => {
      if (col.accessor) {
        const value = row[col.accessor];
        if (
          col.accessor === "amount" ||
          col.accessor === "total_price" ||
          col.accessor === "total_amount"
        ) {
          return `AF ${Number.parseFloat(value || 0).toLocaleString()}`;
        }
        return value || "N/A";
      }
      return "";
    })
  );

  // Add table with enhanced styling
  doc.autoTable({
    head: [tableColumns],
    body: tableRows,
    startY: 50,
    styles: {
      fontSize: 9,
      cellPadding: 3,
    },
    headStyles: {
      fillColor: [33, 150, 243],
      textColor: [255, 255, 255],
      fontStyle: "bold",
    },
    alternateRowStyles: {
      fillColor: [245, 245, 245],
    },
    margin: { top: 50 },
  });

  doc.save(
    `${title.toLowerCase().replace(/\s+/g, "-")}-${
      new Date().toISOString().split("T")[0]
    }.pdf`
  );
};

const downloadExcel = (title, data, columns) => {
  const worksheet = XLSX.utils.json_to_sheet(
    data.map((row) => {
      const newRow = {};
      columns.forEach((col) => {
        if (col.accessor) {
          const value = row[col.accessor];
          if (
            col.accessor === "amount" ||
            col.accessor === "total_price" ||
            col.accessor === "total_amount"
          ) {
            newRow[col.header] = Number.parseFloat(value || 0);
          } else {
            newRow[col.header] = value || "N/A";
          }
        }
      });
      return newRow;
    })
  );

  // Add some styling to the worksheet
  const range = XLSX.utils.decode_range(worksheet["!ref"]);
  for (let C = range.s.c; C <= range.e.c; ++C) {
    const address = XLSX.utils.encode_col(C) + "1";
    if (!worksheet[address]) continue;
    worksheet[address].s = {
      font: { bold: true },
      fill: { fgColor: { rgb: "2196F3" } },
    };
  }

  const workbook = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(workbook, worksheet, title.substring(0, 31)); // Excel sheet name limit
  XLSX.writeFile(
    workbook,
    `${title.toLowerCase().replace(/\s+/g, "-")}-${
      new Date().toISOString().split("T")[0]
    }.xlsx`
  );
};

// Enhanced Data Table Component
const DataTable = ({ title, columns, data }) => {
  const theme = useTheme();

  const getStatusChip = (status) => {
    const statusConfig = {
      Paid: {
        color: "success",
        icon: <CheckCircle sx={{ fontSize: 16 }} />,
        sx: {
          backgroundColor: alpha(theme.palette.success.main, 0.1),
          color: "success.main",
        },
      },
      Pending: {
        color: "warning",
        icon: <Warning sx={{ fontSize: 16 }} />,
        sx: {
          backgroundColor: alpha(theme.palette.warning.main, 0.1),
          color: "warning.main",
        },
      },
      Cancelled: {
        color: "error",
        icon: <Cancel sx={{ fontSize: 16 }} />,
        sx: {
          backgroundColor: alpha(theme.palette.error.main, 0.1),
          color: "error.main",
        },
      },
    };

    const config = statusConfig[status] || {
      color: "default",
      sx: { backgroundColor: alpha(theme.palette.grey[500], 0.1) },
    };

    return (
      <Chip
        label={status}
        size="small"
        icon={config.icon}
        sx={{
          fontWeight: 600,
          ...config.sx,
        }}
      />
    );
  };

  const renderCellContent = (col, row) => {
    if (col.accessor === "payment_status") {
      return getStatusChip(row[col.accessor]);
    }
    if (col.accessor === "file" && row[col.accessor]) {
      return (
        <Tooltip title="View File">
          <IconButton
            component="a"
            href={row[col.accessor]}
            target="_blank"
            rel="noopener noreferrer"
            color="primary"
            size="small"
          >
            <Visibility />
          </IconButton>
        </Tooltip>
      );
    }
    if (
      col.accessor === "amount" ||
      col.accessor === "total_price" ||
      col.accessor === "total_amount"
    ) {
      return (
        <Typography
          variant="body2"
          sx={{ fontWeight: 600, color: "success.main" }}
        >
          ${Number.parseFloat(row[col.accessor] || 0).toLocaleString()}
        </Typography>
      );
    }
    if (col.render) {
      return col.render(row);
    }
    if (col.accessor) {
      return row[col.accessor] || "N/A";
    }
    return "";
  };

  return (
    <Fade in timeout={500}>
      <Card
        sx={{
          mt: 4,
          borderRadius: 3,
          overflow: "hidden",
          boxShadow: theme.shadows[8],
        }}
      >
        <CardHeader
          sx={{
            background: `linear-gradient(135deg, ${alpha(
              theme.palette.primary.main,
              0.1
            )} 0%, ${alpha(theme.palette.primary.main, 0.05)} 100%)`,
            borderBottom: `1px solid ${alpha(theme.palette.primary.main, 0.1)}`,
          }}
          title={
            <Typography
              variant="h5"
              sx={{ fontWeight: 700, color: "text.primary" }}
            >
              {title}
            </Typography>
          }
          action={
            <Stack direction="row" spacing={1}>
              <Tooltip title="Download as PDF">
                <Button
                  variant="outlined"
                  size="small"
                  startIcon={<PictureAsPdf />}
                  onClick={() => downloadPDF(title, data, columns)}
                  disabled={!data || data.length === 0}
                  sx={{
                    textTransform: "none",
                    borderRadius: 2,
                  }}
                >
                  PDF
                </Button>
              </Tooltip>
              <Tooltip title="Download as Excel">
                <Button
                  variant="outlined"
                  size="small"
                  startIcon={<TableChart />}
                  onClick={() => downloadExcel(title, data, columns)}
                  disabled={!data || data.length === 0}
                  sx={{
                    textTransform: "none",
                    borderRadius: 2,
                  }}
                >
                  Excel
                </Button>
              </Tooltip>
            </Stack>
          }
        />
        <CardContent sx={{ p: 0 }}>
          {data && data.length > 0 ? (
            <TableContainer>
              <Table>
                <TableHead>
                  <TableRow
                    sx={{
                      backgroundColor: alpha(theme.palette.primary.main, 0.08),
                    }}
                  >
                    {columns.map((col, index) => (
                      <TableCell
                        key={index}
                        sx={{
                          fontWeight: 700,
                          color: "primary.main",
                          fontSize: "0.875rem",
                          textTransform: "uppercase",
                          letterSpacing: "0.5px",
                        }}
                      >
                        {col.header}
                      </TableCell>
                    ))}
                  </TableRow>
                </TableHead>
                <TableBody>
                  {data.map((row, rowIndex) => (
                    <TableRow
                      key={rowIndex}
                      sx={{
                        "&:hover": {
                          backgroundColor: alpha(
                            theme.palette.primary.main,
                            0.04
                          ),
                        },
                        "&:nth-of-type(even)": {
                          backgroundColor: alpha(theme.palette.grey[50], 0.5),
                        },
                      }}
                    >
                      {columns.map((col, colIndex) => (
                        <TableCell key={colIndex} sx={{ py: 2 }}>
                          {renderCellContent(col, row)}
                        </TableCell>
                      ))}
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          ) : (
            <Box
              sx={{
                textAlign: "center",
                py: 8,
                px: 4,
              }}
            >
              <Assessment
                sx={{
                  fontSize: 80,
                  color: "text.disabled",
                  mb: 2,
                }}
              />
              <Typography
                variant="h5"
                sx={{ color: "text.secondary", mb: 1, fontWeight: 600 }}
              >
                No data available
              </Typography>
              <Typography variant="body1" sx={{ color: "text.disabled" }}>
                There's no data to display for the selected period.
              </Typography>
            </Box>
          )}
        </CardContent>
      </Card>
    </Fade>
  );
};

function ComprehensiveReportPage() {
  const { t } = useTranslation();
  const theme = useTheme();
  const [dateRange, setDateRange] = useState({
    type: "overall",
    start_date: "",
    end_date: "",
  });
  const [activeReport, setActiveReport] = useState(null);
  const [authToken, setAuthToken] = useState(
    localStorage.getItem("authToken") || ""
  );

  const getEndpoint = useCallback(() => {
    const params = new URLSearchParams();
    if (dateRange.type !== "overall") {
      params.append("type", dateRange.type);
    }
    if (dateRange.start_date) {
      params.append("start_date", dateRange.start_date);
    }
    if (dateRange.end_date) {
      params.append("end_date", dateRange.end_date);
    }
    const queryString = params.toString();
    return `comprehensive-reports/${queryString ? `?${queryString}` : ""}`;
  }, [dateRange]);

  const {
    data: reportData,
    isLoading,
    isError,
    error,
    refetch,
  } = useFetchObjects(
    [
      "comprehensiveReports",
      dateRange.type,
      dateRange.start_date,
      dateRange.end_date,
    ],
    getEndpoint(),
    authToken
  );

  useEffect(() => {
    refetch();
    setActiveReport(null);
  }, [dateRange, refetch]);

  const handleSummaryCardClick = (reportType) => {
    setActiveReport(reportType);
  };

  const getDetailedTableColumns = (reportType) => {
    switch (reportType) {
      case "sales":
        return [
          { header: "ID", accessor: "id" },
          { header: "Type", accessor: "type" },
          { header: "Date", accessor: "date" },
          { header: "Customer", accessor: "customer" },
          { header: "Doctor", accessor: "doctor" },
          { header: "Amount", accessor: "amount" },
          { header: "Status", accessor: "payment_status" },
          {
            header: "Items",
            render: (row) => (
              <Box sx={{ display: "flex", flexDirection: "column", gap: 0.5 }}>
                {row.items &&
                  row.items.map((item, i) => (
                    <Chip
                      key={i}
                      label={`${item.medicine} (x${item.quantity}) @ AF ${item.sell_price}`}
                      size="small"
                      variant="outlined"
                      sx={{ fontSize: "0.75rem", height: "auto", py: 0.5 }}
                    />
                  ))}
              </Box>
            ),
          },
        ];
      case "expenses":
        return [
          { header: "ID", accessor: "id" },
          { header: "Date", accessor: "date" },
          { header: "Time", accessor: "time" },
          { header: "Type", accessor: "type" },
          { header: "Amount", accessor: "amount" },
          { header: "Description", accessor: "description" },
          { header: "File", accessor: "file" },
        ];
      case "prescriptionsByDoctor":
        return [
          { header: "Doctor", accessor: "doctor_name" },
          { header: "Total Prescriptions", accessor: "total_prescriptions" },
          { header: "Total Amount", accessor: "total_amount" },
          { header: "Unique Patients", accessor: "unique_patients_served" },
          {
            header: "Recent Prescriptions",
            render: (row) => (
              <Box>
                {row.prescriptions &&
                  row.prescriptions.slice(0, 3).map((p, i) => (
                    <Typography
                      key={i}
                      variant="body2"
                      sx={{ mb: 0.5, fontSize: "0.75rem" }}
                    >
                      #{p.id} - {p.patient} - ${p.total_price} (
                      {p.payment_status})
                    </Typography>
                  ))}
                {row.prescriptions && row.prescriptions.length > 3 && (
                  <Typography
                    variant="body2"
                    sx={{ color: "text.secondary", fontSize: "0.75rem" }}
                  >
                    +{row.prescriptions.length - 3} more...
                  </Typography>
                )}
              </Box>
            ),
          },
        ];
      case "cashflow":
        return [
          { header: "Date", accessor: "date" },
          { header: "Type", accessor: "type" },
          { header: "Description", accessor: "description" },
          { header: "Amount", accessor: "amount" },
          {
            header: "Direction",
            render: (row) => (
              <Chip
                label={row.direction}
                size="small"
                icon={
                  row.direction === "inflow" ? <TrendingUp /> : <TrendingDown />
                }
                sx={{
                  backgroundColor:
                    row.direction === "inflow"
                      ? alpha(theme.palette.success.main, 0.1)
                      : alpha(theme.palette.error.main, 0.1),
                  color:
                    row.direction === "inflow" ? "success.main" : "error.main",
                  fontWeight: 600,
                }}
              />
            ),
          },
        ];
      default:
        return [];
    }
  };

  const getDetailedTableTitle = (reportType) => {
    const typeMap = {
      sales: "Detailed Sales Transactions",
      expenses: "Detailed Expense Records",
      prescriptionsByDoctor: "Prescriptions Grouped by Doctor",
      cashflow: "Detailed Cash Flow Movements",
    };
    return typeMap[reportType] || "Detailed Report";
  };

  const getDetailedTableData = (reportType) => {
    if (!reportData) return [];

    switch (reportType) {
      case "sales":
        return reportData.sales_report?.details || [];
      case "expenses":
        return reportData.expenses_report?.details || [];
      case "prescriptionsByDoctor":
        return reportData.prescriptions_by_doctor_report?.details || [];
      case "cashflow":
        return reportData.cashflow_report?.details || [];
      default:
        return [];
    }
  };

  const renderSummaryCards = () => {
    if (!reportData) return null;

    const cardData = [
      {
        title: "Total Sales",
        value: reportData.sales_report?.summary?.total_sales_amount,
        unit: "AF ",
        icon: AttachMoney,
        color: "success",
        subtitle: `${
          reportData.sales_report?.summary?.total_transactions || 0
        } transactions`,
        onClick: () => handleSummaryCardClick("sales"),
      },
      {
        title: "Total Expenses",
        value: reportData.expenses_report?.summary?.total_expenses_recorded,
        unit: "AF ",
        icon: Receipt,
        color: "error",
        subtitle: `${
          reportData.expenses_report?.summary?.total_expense_entries || 0
        } entries`,
        onClick: () => handleSummaryCardClick("expenses"),
      },
      {
        title: "Prescriptions by Doctors",
        value:
          reportData.prescriptions_by_doctor_report?.summary
            ?.total_prescriptions_overall,
        icon: LocalHospital,
        color: "info",
        subtitle: `${
          reportData.prescriptions_by_doctor_report?.summary
            ?.total_doctors_prescribing || 0
        } doctors`,
        onClick: () => handleSummaryCardClick("prescriptionsByDoctor"),
      },
      {
        title: "Net Cash Flow",
        value: reportData.cashflow_report?.summary?.net_cashflow,
        unit: "AF ",
        icon: AccountBalance,
        color:
          reportData.cashflow_report?.summary?.net_cashflow >= 0
            ? "success"
            : "error",
        subtitle: `In: AF ${
          reportData.cashflow_report?.summary?.total_inflows?.toLocaleString() ||
          0
        } | Out: AF ${
          reportData.cashflow_report?.summary?.total_outflows?.toLocaleString() ||
          0
        }`,
        onClick: () => handleSummaryCardClick("cashflow"),
      },
      {
        title: "Total Income",
        value: reportData.income_expense_statement?.total_income,
        unit: "AF ",
        icon: TrendingUp,
        color: "success",
        onClick: () => setActiveReport("incomeExpenseStatement"),
      },
      {
        title: "Total Revenue",
        value: reportData.revenue_report?.total_revenue,
        unit: "AF ",
        icon: Assessment,
        color: "secondary",
        onClick: () => setActiveReport("revenue"),
      },
    ];

    return (
      <Grid container spacing={3} sx={{ mb: 4 }}>
        {cardData.map((card, index) => (
          <Grid item xs={12} sm={6} md={4} key={index}>
            <ReportCard {...card} />
          </Grid>
        ))}
      </Grid>
    );
  };

  const renderCashflowBreakdown = () => {
    if (!reportData?.cashflow_report?.summary) return null;

    const { inflow_details, outflow_details } =
      reportData.cashflow_report.summary;

    return (
      <Grid container spacing={3} sx={{ mt: 2 }}>
        <Grid item xs={12} md={6}>
          <Card
            sx={{
              background: `linear-gradient(135deg, ${alpha(
                theme.palette.success.main,
                0.1
              )} 0%, ${alpha(theme.palette.success.main, 0.05)} 100%)`,
              border: `1px solid ${alpha(theme.palette.success.main, 0.2)}`,
              borderRadius: 3,
            }}
          >
            <CardContent sx={{ p: 3 }}>
              <Typography
                variant="h6"
                sx={{
                  color: "success.main",
                  fontWeight: 700,
                  mb: 2,
                  display: "flex",
                  alignItems: "center",
                }}
              >
                <TrendingUp sx={{ mr: 1 }} />
                Cash Inflows
              </Typography>
              <List dense>
                <ListItem sx={{ px: 0 }}>
                  <ListItemText
                    primary={
                      <Typography variant="body1" sx={{ fontWeight: 600 }}>
                        Sales Revenue
                      </Typography>
                    }
                    secondary={
                      <Typography
                        variant="h6"
                        sx={{ color: "success.main", fontWeight: 700 }}
                      >
                        ${inflow_details?.sales_inflow?.toLocaleString() || 0}
                      </Typography>
                    }
                  />
                </ListItem>
              </List>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} md={6}>
          <Card
            sx={{
              background: `linear-gradient(135deg, ${alpha(
                theme.palette.error.main,
                0.1
              )} 0%, ${alpha(theme.palette.error.main, 0.05)} 100%)`,
              border: `1px solid ${alpha(theme.palette.error.main, 0.2)}`,
              borderRadius: 3,
            }}
          >
            <CardContent sx={{ p: 3 }}>
              <Typography
                variant="h6"
                sx={{
                  color: "error.main",
                  fontWeight: 700,
                  mb: 2,
                  display: "flex",
                  alignItems: "center",
                }}
              >
                <TrendingDown sx={{ mr: 1 }} />
                Cash Outflows
              </Typography>
              <List dense>
                {[
                  {
                    label: "Expense Payments",
                    value: outflow_details?.expense_payments,
                  },
                  {
                    label: "Purchase Payments",
                    value: outflow_details?.purchase_payments,
                  },
                  {
                    label: "Employee Advances",
                    value: outflow_details?.employee_advances,
                  },
                  {
                    label: "Employee Bonuses",
                    value: outflow_details?.employee_bonuses,
                  },
                  {
                    label: "Payroll Payments",
                    value: outflow_details?.employee_payroll_payments,
                  },
                ].map((item, index) => (
                  <ListItem key={index} sx={{ px: 0, py: 0.5 }}>
                    <ListItemText
                      primary={
                        <Typography
                          variant="body2"
                          sx={{ fontSize: "0.875rem" }}
                        >
                          {item.label}
                        </Typography>
                      }
                      secondary={
                        <Typography
                          variant="body1"
                          sx={{ color: "error.main", fontWeight: 600 }}
                        >
                          ${item.value?.toLocaleString() || 0}
                        </Typography>
                      }
                    />
                  </ListItem>
                ))}
              </List>
            </CardContent>
          </Card>
        </Grid>
      </Grid>
    );
  };

  const renderSpecialReports = () => {
    if (!reportData) return null;

    if (activeReport === "incomeExpenseStatement") {
      const statement = reportData.income_expense_statement;

      return (
        <Fade in timeout={500}>
          <Card sx={{ mt: 3, borderRadius: 3, overflow: "hidden" }}>
            <CardHeader
              sx={{
                background: `linear-gradient(135deg, ${alpha(
                  theme.palette.primary.main,
                  0.1
                )} 0%, ${alpha(theme.palette.primary.main, 0.05)} 100%)`,
              }}
              title={
                <Typography variant="h5" sx={{ fontWeight: 700 }}>
                  Income & Expense Statement
                </Typography>
              }
            />
            <CardContent sx={{ p: 4 }}>
              {statement ? (
                <Grid container spacing={4}>
                  <Grid item xs={12} md={4}>
                    <Box
                      sx={{
                        textAlign: "center",
                        p: 3,
                        borderRadius: 3,
                        background: `linear-gradient(135deg, ${alpha(
                          theme.palette.success.main,
                          0.1
                        )} 0%, ${alpha(
                          theme.palette.success.main,
                          0.05
                        )} 100%)`,
                        border: `1px solid ${alpha(
                          theme.palette.success.main,
                          0.2
                        )}`,
                      }}
                    >
                      <TrendingUp
                        sx={{ fontSize: 48, color: "success.main", mb: 2 }}
                      />
                      <Typography
                        variant="h6"
                        sx={{ color: "text.secondary", mb: 1 }}
                      >
                        Total Income
                      </Typography>
                      <Typography
                        variant="h4"
                        sx={{ fontWeight: 700, color: "success.main" }}
                      >
                        ${statement.total_income?.toLocaleString()}
                      </Typography>
                    </Box>
                  </Grid>
                  <Grid item xs={12} md={4}>
                    <Box
                      sx={{
                        textAlign: "center",
                        p: 3,
                        borderRadius: 3,
                        background: `linear-gradient(135deg, ${alpha(
                          theme.palette.error.main,
                          0.1
                        )} 0%, ${alpha(theme.palette.error.main, 0.05)} 100%)`,
                        border: `1px solid ${alpha(
                          theme.palette.error.main,
                          0.2
                        )}`,
                      }}
                    >
                      <TrendingDown
                        sx={{ fontSize: 48, color: "error.main", mb: 2 }}
                      />
                      <Typography
                        variant="h6"
                        sx={{ color: "text.secondary", mb: 1 }}
                      >
                        Total Expenses
                      </Typography>
                      <Typography
                        variant="h4"
                        sx={{ fontWeight: 700, color: "error.main" }}
                      >
                        ${statement.total_expenses?.toLocaleString()}
                      </Typography>
                    </Box>
                  </Grid>
                  <Grid item xs={12} md={4}>
                    <Box
                      sx={{
                        textAlign: "center",
                        p: 3,
                        borderRadius: 3,
                        background: `linear-gradient(135deg, ${alpha(
                          statement.net_profit_loss >= 0
                            ? theme.palette.success.main
                            : theme.palette.error.main,
                          0.1
                        )} 0%, ${alpha(
                          statement.net_profit_loss >= 0
                            ? theme.palette.success.main
                            : theme.palette.error.main,
                          0.05
                        )} 100%)`,
                        border: `1px solid ${alpha(
                          statement.net_profit_loss >= 0
                            ? theme.palette.success.main
                            : theme.palette.error.main,
                          0.2
                        )}`,
                      }}
                    >
                      <Assessment
                        sx={{
                          fontSize: 48,
                          color:
                            statement.net_profit_loss >= 0
                              ? "success.main"
                              : "error.main",
                          mb: 2,
                        }}
                      />
                      <Typography
                        variant="h6"
                        sx={{ color: "text.secondary", mb: 1 }}
                      >
                        Net Profit/Loss
                      </Typography>
                      <Typography
                        variant="h4"
                        sx={{
                          fontWeight: 700,
                          color:
                            statement.net_profit_loss >= 0
                              ? "success.main"
                              : "error.main",
                        }}
                      >
                        ${statement.net_profit_loss?.toLocaleString()}
                      </Typography>
                    </Box>
                  </Grid>
                </Grid>
              ) : (
                <Box sx={{ textAlign: "center", py: 8 }}>
                  <Assessment
                    sx={{ fontSize: 80, color: "text.disabled", mb: 2 }}
                  />
                  <Typography variant="h6" sx={{ color: "text.secondary" }}>
                    No income/expense data available
                  </Typography>
                </Box>
              )}
            </CardContent>
          </Card>
        </Fade>
      );
    }

    if (activeReport === "revenue") {
      const revenue = reportData.revenue_report;

      return (
        <Fade in timeout={500}>
          <Card sx={{ mt: 3, borderRadius: 3, overflow: "hidden" }}>
            <CardHeader
              sx={{
                background: `linear-gradient(135deg, ${alpha(
                  theme.palette.secondary.main,
                  0.1
                )} 0%, ${alpha(theme.palette.secondary.main, 0.05)} 100%)`,
              }}
              title={
                <Typography variant="h5" sx={{ fontWeight: 700 }}>
                  Revenue Report
                </Typography>
              }
            />
            <CardContent sx={{ p: 6 }}>
              {revenue ? (
                <Box sx={{ textAlign: "center" }}>
                  <Assessment
                    sx={{ fontSize: 80, color: "secondary.main", mb: 3 }}
                  />
                  <Typography
                    variant="h6"
                    sx={{ color: "text.secondary", mb: 2 }}
                  >
                    Total Revenue
                  </Typography>
                  <Typography
                    variant="h3"
                    sx={{ fontWeight: 700, color: "secondary.main" }}
                  >
                    ${revenue.total_revenue?.toLocaleString()}
                  </Typography>
                </Box>
              ) : (
                <Box sx={{ textAlign: "center", py: 8 }}>
                  <Assessment
                    sx={{ fontSize: 80, color: "text.disabled", mb: 2 }}
                  />
                  <Typography variant="h6" sx={{ color: "text.secondary" }}>
                    No revenue data available
                  </Typography>
                </Box>
              )}
            </CardContent>
          </Card>
        </Fade>
      );
    }

    return null;
  };

  return (
    <Box
      sx={{ flexGrow: 1, bgcolor: "background.default", minHeight: "100vh" }}
    >
      <AppBar
        position="static"
        elevation={0}
        sx={{
          background: `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.primary.dark} 100%)`,
        }}
      >
        <Toolbar sx={{ py: 1 }}>
          <Assessment sx={{ mr: 2, fontSize: 32 }} />
          <Typography
            variant="h4"
            component="div"
            sx={{ flexGrow: 1, fontWeight: 700 }}
          >
            Comprehensive Financial Reports
          </Typography>
          {reportData && (
            <Typography variant="body2" sx={{ mr: 2, opacity: 0.9 }}>
              {reportData.report_start_date !== "N/A" &&
              reportData.report_end_date !== "N/A"
                ? `${reportData.report_start_date} to ${reportData.report_end_date}`
                : reportData.report_period_type}
            </Typography>
          )}
          <Tooltip title="Refresh Data">
            <IconButton
              color="inherit"
              onClick={() => refetch()}
              disabled={isLoading}
            >
              {isLoading ? (
                <CircularProgress size={24} color="inherit" />
              ) : (
                <Refresh />
              )}
            </IconButton>
          </Tooltip>
        </Toolbar>
        {isLoading && <LinearProgress />}
      </AppBar>

      <Container maxWidth="xl" sx={{ py: 4 }}>
        <DateRangeSelector onDateRangeChange={setDateRange} />

        {isLoading && (
          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              justifyContent: "center",
              py: 12,
            }}
          >
            <CircularProgress size={60} sx={{ mb: 3 }} />
            <Typography variant="h5" sx={{ mb: 1, fontWeight: 600 }}>
              Loading reports...
            </Typography>
            <Typography variant="body1" sx={{ color: "text.secondary" }}>
              Please wait while we fetch your financial data
            </Typography>
          </Box>
        )}

        {isError && (
          <Alert
            severity="error"
            sx={{
              mb: 3,
              borderRadius: 2,
              "& .MuiAlert-message": {
                fontSize: "1rem",
              },
            }}
          >
            <Typography variant="h6" sx={{ mb: 1 }}>
              Error loading reports
            </Typography>
            {error?.message}
          </Alert>
        )}

        {!activeReport && renderSummaryCards()}

        {activeReport === "cashflow" && renderCashflowBreakdown()}

        {activeReport && (
          <Box>
            <Box sx={{ display: "flex", alignItems: "center", mb: 3 }}>
              <Button
                startIcon={<ArrowBack />}
                onClick={() => setActiveReport(null)}
                variant="outlined"
                sx={{
                  mr: 2,
                  textTransform: "none",
                  borderRadius: 2,
                  px: 3,
                  py: 1,
                }}
              >
                Back to Summary
              </Button>
            </Box>

            {renderSpecialReports()}

            {!["incomeExpenseStatement", "revenue"].includes(activeReport) && (
              <DataTable
                title={getDetailedTableTitle(activeReport)}
                columns={getDetailedTableColumns(activeReport)}
                data={getDetailedTableData(activeReport)}
              />
            )}

            {activeReport &&
              !isLoading &&
              getDetailedTableData(activeReport).length === 0 &&
              !["incomeExpenseStatement", "revenue"].includes(activeReport) && (
                <Box sx={{ textAlign: "center", py: 8 }}>
                  <Assessment
                    sx={{ fontSize: 80, color: "text.disabled", mb: 2 }}
                  />
                  <Typography
                    variant="h5"
                    sx={{ color: "text.secondary", mb: 1, fontWeight: 600 }}
                  >
                    No detailed data available
                  </Typography>
                  <Typography variant="body1" sx={{ color: "text.disabled" }}>
                    There's no detailed data available for this period
                  </Typography>
                </Box>
              )}
          </Box>
        )}
      </Container>
    </Box>
  );
}

export default ComprehensiveReportPage;
